import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumContainerStatus } from "./enums/enumContainerStatus.generated";

// Model Objects

export class ContainerReadOnly extends ModelObject<ContainerReadOnly> {

    /**
    * Timestamp at which the container was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Container extends ModelObject<Container> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Etch Number
    * Not Nullable
    * Max Length : 1000
    */
    public etchNo: string = "";
    /**
    * Notes/Comments
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Permanent Label currently on this container.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public permLabel: string = "";
    /**
    * Package type id
    */
    public packageTypeId: Guid = Guid.createEmpty();
    public readOnly: ContainerReadOnly = new ContainerReadOnly();
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.etchNo = jsonSerializedObject.etchNo;
          this.notes = jsonSerializedObject.notes;
          this.permLabel = jsonSerializedObject.permLabel;
          this.packageTypeId = Guid.parseOrEmpty(jsonSerializedObject.packageTypeId);
          this.readOnly = new ContainerReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.etchNo = this.etchNo;
        toRet.notes = this.notes;
        toRet.permLabel = this.permLabel;
        toRet.packageTypeId = this.packageTypeId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Container.Id cannot be empty"},
            {field: "etchNo", rule: (v: any) => v == null || v.length <= 1000 || "EtchNo has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "permLabel", rule: (v: any) => v == null || v.length !== 0 || "PermLabel cannot be empty"},
            {field: "permLabel", rule: (v: any) => v == null || v.length <= 1000 || "PermLabel has too many characters (1000)"},
            {field: "packageTypeId", rule: (v: any) => !v.isEmpty() || "Container.PackageTypeId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ContainerHistory extends ModelObject<ContainerHistory> {

    /**
    * Timestamp at which the container history item was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    public id: Guid = Guid.createEmpty();
    /**
    * Status after this history item occurred.
    */
    public newStatus: EnumContainerStatus = EnumContainerStatus.cleanAndIn;
    private _orderId: Guid | null = null;
    /**
    * Order for which the container was shipped, if known.
    */
    public get orderId(): Guid | null { return this._orderId }
    public set orderId(value: Guid | null) {
      this._orderId = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet which has the container (if any).
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _productBatchId: Guid | null = null;
    /**
    * Product Batch now in the container (if any).
    */
    public get productBatchId(): Guid | null { return this._productBatchId }
    public set productBatchId(value: Guid | null) {
      this._productBatchId = value === undefined ? null : value;
    }
    /**
    * User Causing History item.
    */
    public userId: Guid = Guid.createEmpty();
    private _filledFromAssemblyId: Guid | null = null;
    /**
    * If known, the assembly producing the product this container was filled from. Container should either be filled from Assembly or Process, but not both.
    */
    public get filledFromAssemblyId(): Guid | null { return this._filledFromAssemblyId }
    public set filledFromAssemblyId(value: Guid | null) {
      this._filledFromAssemblyId = value === undefined ? null : value;
    }
    /**
    * Container Id.
    */
    public containerId: Guid = Guid.createEmpty();
    private _filledFromProcessId: Guid | null = null;
    /**
    * If known, the process producing the product this container was filled from. Container should either be filled from Assembly or Process, but not both.
    */
    public get filledFromProcessId(): Guid | null { return this._filledFromProcessId }
    public set filledFromProcessId(value: Guid | null) {
      this._filledFromProcessId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.newStatus = EnumContainerStatus.getByValue(jsonSerializedObject.newStatus, this.newStatus);
          this.orderId = jsonSerializedObject.orderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.productBatchId = jsonSerializedObject.productBatchId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBatchId);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.filledFromAssemblyId = jsonSerializedObject.filledFromAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.filledFromAssemblyId);
          this.containerId = Guid.parseOrEmpty(jsonSerializedObject.containerId);
          this.filledFromProcessId = jsonSerializedObject.filledFromProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.filledFromProcessId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.id = this.id.toString();
        toRet.newStatus = this.newStatus.value;
        toRet.orderId = this.orderId == null ? null : this.orderId.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.productBatchId = this.productBatchId == null ? null : this.productBatchId.toString();
        toRet.userId = this.userId.toString();
        toRet.filledFromAssemblyId = this.filledFromAssemblyId == null ? null : this.filledFromAssemblyId.toString();
        toRet.containerId = this.containerId.toString();
        toRet.filledFromProcessId = this.filledFromProcessId == null ? null : this.filledFromProcessId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ContainerHistory.Id cannot be empty"},
            {field: "orderId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerHistory.OrderId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerHistory.OutletId cannot be empty"},
            {field: "productBatchId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerHistory.ProductBatchId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "ContainerHistory.UserId cannot be empty"},
            {field: "filledFromAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerHistory.FilledFromAssemblyId cannot be empty"},
            {field: "containerId", rule: (v: any) => !v.isEmpty() || "ContainerHistory.ContainerId cannot be empty"},
            {field: "filledFromProcessId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerHistory.FilledFromProcessId cannot be empty"},
    ];
}


export class ContainerFilter extends ModelObject<ContainerFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitContainerStatus: EnumContainerStatus[] | null = null;
    public get limitContainerStatus(): EnumContainerStatus[] | null { return this._limitContainerStatus }
    public set limitContainerStatus(value: EnumContainerStatus[] | null) {
      this._limitContainerStatus = value === undefined ? null : value;
    }
    private _limitOutletId: Guid | null = null;
    /**
    * Includes containers of an outlet based on its latest history record.
    */
    public get limitOutletId(): Guid | null { return this._limitOutletId }
    public set limitOutletId(value: Guid | null) {
      this._limitOutletId = value === undefined ? null : value;
    }
    private _containerIds: Guid[] | null = null;
    /**
    * Containers to include
    */
    public get containerIds(): Guid[] | null { return this._containerIds }
    public set containerIds(value: Guid[] | null) {
      this._containerIds = value === undefined ? null : value;
    }
    private _filledFromProcessIds: Guid[] | null = null;
    /**
    * Includes containers whose current contents are from the given processes.
    */
    public get filledFromProcessIds(): Guid[] | null { return this._filledFromProcessIds }
    public set filledFromProcessIds(value: Guid[] | null) {
      this._filledFromProcessIds = value === undefined ? null : value;
    }
    private _filledFromAssemblyIds: Guid[] | null = null;
    /**
    * Includes containers whose current contents are from the given assemblies.
    */
    public get filledFromAssemblyIds(): Guid[] | null { return this._filledFromAssemblyIds }
    public set filledFromAssemblyIds(value: Guid[] | null) {
      this._filledFromAssemblyIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitContainerStatus = jsonSerializedObject.limitContainerStatus == null ? null : jsonSerializedObject.limitContainerStatus.map((v: any) => EnumContainerStatus.getByValue(v));
          this.limitOutletId = jsonSerializedObject.limitOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.limitOutletId);
          this.containerIds = jsonSerializedObject.containerIds == null ? null : jsonSerializedObject.containerIds.map((v: any) => Guid.parseOrEmpty(v));
          this.filledFromProcessIds = jsonSerializedObject.filledFromProcessIds == null ? null : jsonSerializedObject.filledFromProcessIds.map((v: any) => Guid.parseOrEmpty(v));
          this.filledFromAssemblyIds = jsonSerializedObject.filledFromAssemblyIds == null ? null : jsonSerializedObject.filledFromAssemblyIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitContainerStatus = this.limitContainerStatus == null ? null : this.limitContainerStatus.map(v => v.value);
        toRet.limitOutletId = this.limitOutletId == null ? null : this.limitOutletId.toString();
        toRet.containerIds = this.containerIds == null ? null : this.containerIds.map(v => v.toString());
        toRet.filledFromProcessIds = this.filledFromProcessIds == null ? null : this.filledFromProcessIds.map(v => v.toString());
        toRet.filledFromAssemblyIds = this.filledFromAssemblyIds == null ? null : this.filledFromAssemblyIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "limitOutletId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerFilter.LimitOutletId cannot be empty"},
    ];
}


export class ContainerWithStatus extends ModelObject<ContainerWithStatus> {

    public container: Container = new Container();
    public latestHistory: ContainerHistory = new ContainerHistory();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.container = new Container(jsonSerializedObject.container);
          this.latestHistory = new ContainerHistory(jsonSerializedObject.latestHistory);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.container = this.container.toJsonSerializedObject();
        toRet.latestHistory = this.latestHistory.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "container", rule: (v: any) => !!v || "container is required"},
            {field: "container", rule: (v: any) => v.validate()},
            {field: "latestHistory", rule: (v: any) => !!v || "latestHistory is required"},
            {field: "latestHistory", rule: (v: any) => v.validate()},
    ];
}


export class ContainerSummariesResult extends ModelObject<ContainerSummariesResult> {

    public matchingCount: number = 0;
    public containers: ContainerWithStatus[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.containers = jsonSerializedObject.containers.map((v: any) => new ContainerWithStatus(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.containers = this.containers.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "containers", rule: (v: any) => !!v || "containers is required"},
            {field: "containers", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ContainerWithStatus[]).reduce((acc: Array<string>, curr: ContainerWithStatus) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ContainerStatusUpdate extends ModelObject<ContainerStatusUpdate> {

    public containerId: Guid = Guid.createEmpty();
    private _filledFromAssemblyId: Guid | null = null;
    /**
    * If known, the assembly producing the product this container was filled from.
    */
    public get filledFromAssemblyId(): Guid | null { return this._filledFromAssemblyId }
    public set filledFromAssemblyId(value: Guid | null) {
      this._filledFromAssemblyId = value === undefined ? null : value;
    }
    private _productBatchId: Guid | null = null;
    /**
    * Last known container contents if any.
    */
    public get productBatchId(): Guid | null { return this._productBatchId }
    public set productBatchId(value: Guid | null) {
      this._productBatchId = value === undefined ? null : value;
    }
    /**
    * Makes the container lost (assumed empty)
    */
    public isLost: boolean = false;
    /**
    * Makes the container In, will remain full or empty
    */
    public isIn: boolean = false;
    private _orderId: Guid | null = null;
    /**
    * Makes the container Out for the given Order. Container must be full.
    */
    public get orderId(): Guid | null { return this._orderId }
    public set orderId(value: Guid | null) {
      this._orderId = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Makes the container Out to the given outlet, order unknown, container contents must be known.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    /**
    * Makes the container Dirty, if it is both in and empty.
    */
    public needsCleaning: boolean = false;
    /**
    * Makes the container OutOfOrder.
    */
    public isOutOfOrder: boolean = false;
    private _filledFromProcessId: Guid | null = null;
    /**
    * If known, the process producing the product this container was filled from.
    */
    public get filledFromProcessId(): Guid | null { return this._filledFromProcessId }
    public set filledFromProcessId(value: Guid | null) {
      this._filledFromProcessId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.containerId = Guid.parseOrEmpty(jsonSerializedObject.containerId);
          this.filledFromAssemblyId = jsonSerializedObject.filledFromAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.filledFromAssemblyId);
          this.productBatchId = jsonSerializedObject.productBatchId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBatchId);
          this.isLost = jsonSerializedObject.isLost;
          this.isIn = jsonSerializedObject.isIn;
          this.orderId = jsonSerializedObject.orderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.needsCleaning = jsonSerializedObject.needsCleaning;
          this.isOutOfOrder = jsonSerializedObject.isOutOfOrder;
          this.filledFromProcessId = jsonSerializedObject.filledFromProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.filledFromProcessId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.containerId = this.containerId.toString();
        toRet.filledFromAssemblyId = this.filledFromAssemblyId == null ? null : this.filledFromAssemblyId.toString();
        toRet.productBatchId = this.productBatchId == null ? null : this.productBatchId.toString();
        toRet.isLost = this.isLost;
        toRet.isIn = this.isIn;
        toRet.orderId = this.orderId == null ? null : this.orderId.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.needsCleaning = this.needsCleaning;
        toRet.isOutOfOrder = this.isOutOfOrder;
        toRet.filledFromProcessId = this.filledFromProcessId == null ? null : this.filledFromProcessId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "containerId", rule: (v: any) => !v.isEmpty() || "ContainerStatusUpdate.ContainerId cannot be empty"},
            {field: "filledFromAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerStatusUpdate.FilledFromAssemblyId cannot be empty"},
            {field: "productBatchId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerStatusUpdate.ProductBatchId cannot be empty"},
            {field: "orderId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerStatusUpdate.OrderId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerStatusUpdate.OutletId cannot be empty"},
            {field: "filledFromProcessId", rule: (v: any) => v == null || !v.isEmpty() || "ContainerStatusUpdate.FilledFromProcessId cannot be empty"},
    ];
}


export class OutletContainerLogFilter extends ModelObject<OutletContainerLogFilter> {

    private _fromDate: LocalDate | null = null;
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class OutletContainerLogNextUpdate extends ModelObject<OutletContainerLogNextUpdate> {

    public createdAt: Instant = Instant.ofEpochMilli(0);
    public containerStatus: EnumContainerStatus = EnumContainerStatus.cleanAndIn;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.containerStatus = EnumContainerStatus.getByValue(jsonSerializedObject.containerStatus, this.containerStatus);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.containerStatus = this.containerStatus.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class OutletContainerLog extends ModelObject<OutletContainerLog> {

    public containerId: Guid = Guid.createEmpty();
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _productBatchId: Guid | null = null;
    /**
    * Will normally have a value as outlet logs will only be returned for containers which went out, and therefore should have contents.
    */
    public get productBatchId(): Guid | null { return this._productBatchId }
    public set productBatchId(value: Guid | null) {
      this._productBatchId = value === undefined ? null : value;
    }
    private _nextUpdate: OutletContainerLogNextUpdate | null = null;
    public get nextUpdate(): OutletContainerLogNextUpdate | null { return this._nextUpdate }
    public set nextUpdate(value: OutletContainerLogNextUpdate | null) {
      this._nextUpdate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.containerId = Guid.parseOrEmpty(jsonSerializedObject.containerId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.productBatchId = jsonSerializedObject.productBatchId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBatchId);
          this.nextUpdate = jsonSerializedObject.nextUpdate == null ? null : new OutletContainerLogNextUpdate(jsonSerializedObject.nextUpdate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.containerId = this.containerId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.productBatchId = this.productBatchId == null ? null : this.productBatchId.toString();
        toRet.nextUpdate = this.nextUpdate == null ? null : this.nextUpdate?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "containerId", rule: (v: any) => !v.isEmpty() || "OutletContainerLog.ContainerId cannot be empty"},
            {field: "productBatchId", rule: (v: any) => v == null || !v.isEmpty() || "OutletContainerLog.ProductBatchId cannot be empty"},
            {field: "nextUpdate", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class ContainerTreatmentLine extends ModelObject<ContainerTreatmentLine> {

    public outletId: Guid = Guid.createEmpty();
    /**
    * Number of lost containers which were last marked as out at this outlet
    */
    public containersLostAllTime: number = 0;
    /**
    * Number of containers which were marked as lost within the last 6 months, and were last marked as out at this outlet
    */
    public containersLostForLastSixMonths: number = 0;
    /**
    * Average number of days a container spends a the outlet. (Containers which came back from the outlet within last 6 months.)
    */
    public averageDaysAtOutlet: number = 0;
    /**
    * Number of containers currently at the outlet
    */
    public numberOfContainersAtOutlet: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.containersLostAllTime = jsonSerializedObject.containersLostAllTime;
          this.containersLostForLastSixMonths = jsonSerializedObject.containersLostForLastSixMonths;
          this.averageDaysAtOutlet = jsonSerializedObject.averageDaysAtOutlet;
          this.numberOfContainersAtOutlet = jsonSerializedObject.numberOfContainersAtOutlet;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outletId = this.outletId.toString();
        toRet.containersLostAllTime = this.containersLostAllTime;
        toRet.containersLostForLastSixMonths = this.containersLostForLastSixMonths;
        toRet.averageDaysAtOutlet = this.averageDaysAtOutlet;
        toRet.numberOfContainersAtOutlet = this.numberOfContainersAtOutlet;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "ContainerTreatmentLine.OutletId cannot be empty"},
            {field: "containersLostAllTime", rule: (v: any) => v == null || v >= -2147483648 || "ContainersLostAllTime is not greater than minimum allowed value: -2147483648"},
            {field: "containersLostAllTime", rule: (v: any) => v == null || v <= 2147483647 || "ContainersLostAllTime is above maximum allowed value: 2147483647"},
            {field: "containersLostForLastSixMonths", rule: (v: any) => v == null || v >= -2147483648 || "ContainersLostForLastSixMonths is not greater than minimum allowed value: -2147483648"},
            {field: "containersLostForLastSixMonths", rule: (v: any) => v == null || v <= 2147483647 || "ContainersLostForLastSixMonths is above maximum allowed value: 2147483647"},
            {field: "numberOfContainersAtOutlet", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfContainersAtOutlet is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfContainersAtOutlet", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfContainersAtOutlet is above maximum allowed value: 2147483647"},
    ];
}


export class ContainerService extends ServiceBase {

  public async createContainer(tenantId: Guid, container: Container, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    container:container.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/createContainer", req, true, extractor, opts);
  };

  public async getContainer(tenantId: Guid, containerId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Container> {
    const req = {
    tenantId:tenantId.toString(),
    containerId:containerId.toString(),
    };
    const extractor = (response:any) => new Container(response.container);;
    return this.makeJsonRequest("container/v1/getContainer", req, false, extractor, opts);
  };

  public async getContainerByPermLabel(tenantId: Guid, permLabel: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Container> {
    const req = {
    tenantId:tenantId.toString(),
    permLabel:permLabel,
    };
    const extractor = (response:any) => new Container(response.container);;
    return this.makeJsonRequest("container/v1/getContainerByPermLabel", req, false, extractor, opts);
  };

  public async getContainersByPermLabels(tenantId: Guid, permLabels: readonly string[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Container[]> {
    const req = {
    tenantId:tenantId.toString(),
    permLabels:permLabels.map(v => v),
    };
    const extractor = (response:any) => response.containers.map((v: any) => new Container(v));;
    return this.makeJsonRequest("container/v1/getContainersByPermLabels", req, false, extractor, opts);
  };

  public async getContainerSummaries(tenantId: Guid, filter: ContainerFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContainerSummariesResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new ContainerSummariesResult(response.results);;
    return this.makeJsonRequest("container/v1/getContainerSummaries", req, false, extractor, opts);
  };

  public async getHistoryForContainer(tenantId: Guid, containerId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContainerHistory[]> {
    const req = {
    tenantId:tenantId.toString(),
    containerId:containerId.toString(),
    };
    const extractor = (response:any) => response.history.map((v: any) => new ContainerHistory(v));;
    return this.makeJsonRequest("container/v1/getHistoryForContainer", req, false, extractor, opts);
  };

  public async setContainerIsHidden(tenantId: Guid, containerId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    containerId:containerId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/setContainerIsHidden", req, true, extractor, opts);
  };

  public async updateContainer(tenantId: Guid, container: Container, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    container:container.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/updateContainer", req, true, extractor, opts);
  };

  public async updateContainerStatus(tenantId: Guid, containerId: Guid, filledFromAssemblyId: Guid | null, productBatchId: Guid | null, isLost: boolean, isIn: boolean, orderId: Guid | null, outletId: Guid | null, needsCleaning: boolean, isOutOfOrder: boolean, filledFromProcessId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    containerId:containerId.toString(),
    filledFromAssemblyId:filledFromAssemblyId == null ? null : filledFromAssemblyId.toString(),
    productBatchId:productBatchId == null ? null : productBatchId.toString(),
    isLost:isLost,
    isIn:isIn,
    orderId:orderId == null ? null : orderId.toString(),
    outletId:outletId == null ? null : outletId.toString(),
    needsCleaning:needsCleaning,
    isOutOfOrder:isOutOfOrder,
    filledFromProcessId:filledFromProcessId == null ? null : filledFromProcessId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/updateContainerStatus", req, true, extractor, opts);
  };

  public async updateContainerStatuses(tenantId: Guid, updates: readonly ContainerStatusUpdate[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    updates:updates.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/updateContainerStatuses", req, true, extractor, opts);
  };

  public async getContainers(tenantId: Guid, containerIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Container[]> {
    const req = {
    tenantId:tenantId.toString(),
    containerIds:containerIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.containers.map((v: any) => new Container(v));;
    return this.makeJsonRequest("container/v1/getContainers", req, false, extractor, opts);
  };

  public async appendToContainersNotes(tenantId: Guid, containerIds: readonly Guid[], textToAppend: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    containerIds:containerIds.map(v => v.toString()),
    textToAppend:textToAppend,
    };
    const extractor = null;
    return this.makeJsonRequest("container/v1/appendToContainersNotes", req, false, extractor, opts);
  };

  public async getOutletContainerLogs(tenantId: Guid, outletId: Guid, filter: OutletContainerLogFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletContainerLog[]> {
    const req = {
    tenantId:tenantId.toString(),
    outletId:outletId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => response.outletContainerLogs.map((v: any) => new OutletContainerLog(v));;
    return this.makeJsonRequest("container/v1/getOutletContainerLogs", req, false, extractor, opts);
  };

  public async generateContainerTreatmentReport(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContainerTreatmentLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.containerTreatmentLines.map((v: any) => new ContainerTreatmentLine(v));;
    return this.makeJsonRequest("container/v1/generateContainerTreatmentReport", req, false, extractor, opts);
  };

  public async getContainerFillsForAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContainerHistory[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => response.containerHistories.map((v: any) => new ContainerHistory(v));;
    return this.makeJsonRequest("container/v1/getContainerFillsForAssembly", req, false, extractor, opts);
  };

  public async getContainerFillsForProcesses(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContainerHistory[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.containerHistories.map((v: any) => new ContainerHistory(v));;
    return this.makeJsonRequest("container/v1/getContainerFillsForProcesses", req, false, extractor, opts);
  };
}

export const containerService = new ContainerService();